import { createClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import { secureHybridStorage } from './secureHybridStorage'

// Environment variable validation with detailed logging
const validateEnvVars = () => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
  
  // Debug environment variables (remove in production)
  console.log('🔍 Supabase Environment Check:', {
    supabaseUrl: supabaseUrl ? '✅ Set' : '❌ Missing',
    supabaseAnonKey: supabaseAnonKey ? '✅ Set' : '❌ Missing',
    allEnvVars: Object.keys(import.meta.env).filter(key => key.startsWith('VITE_'))
  })
  
  if (!supabaseUrl || !supabaseAnonKey) {
    console.warn('⚠️ Missing Supabase environment variables:', {
      VITE_SUPABASE_URL: supabaseUrl ? 'Set' : 'Missing',
      VITE_SUPABASE_ANON_KEY: supabaseAnonKey ? 'Set' : 'Missing',
      help: 'Check your app/.env file and ensure all VITE_SUPABASE_* variables are set'
    })
    return false
  }
  
  console.log('✅ Supabase environment variables validated successfully')
  return true
}

// Create mock client for development when env vars are missing
const createMockClient = () => {
  console.warn('🔧 Using mock Supabase client for development')
  
  const mockClient = {
    auth: {
      signUp: () => {
        console.warn('Mock: signUp called')
        return Promise.resolve({
          data: null,
          error: new Error('Supabase not configured - using mock client')
        })
      },
      signInWithPassword: () => {
        console.warn('Mock: signInWithPassword called')
        return Promise.resolve({
          data: null,
          error: new Error('Supabase not configured - using mock client')
        })
      },
      signInWithOAuth: () => {
        console.warn('Mock: signInWithOAuth called')
        return Promise.resolve({
          data: null,
          error: new Error('Supabase not configured - using mock client')
        })
      },
      signOut: () => {
        console.warn('Mock: signOut called')
        return Promise.resolve({ error: null })
      },
      getSession: () => {
        console.warn('Mock: getSession called')
        return Promise.resolve({
          data: { session: null },
          error: null
        })
      },
      exchangeCodeForSession: () => {
        console.warn('Mock: exchangeCodeForSession called')
        return Promise.resolve({
          data: null,
          error: new Error('Mock client - no code exchange')
        })
      },
      onAuthStateChange: () => {
        console.warn('Mock: onAuthStateChange called')
        return {
          data: {
            subscription: {
              unsubscribe: () => console.warn('Mock: auth subscription unsubscribed')
            }
          }
        }
      },
      getUser: () => {
        console.warn('Mock: getUser called')
        return Promise.resolve({
          data: { user: null },
          error: null
        })
      }
    },
    from: (table: string) => {
      console.warn(`Mock: database query on table "${table}"`)
      return {
        select: () => Promise.resolve({ data: [], error: null }),
        insert: () => Promise.resolve({ data: null, error: null }),
        update: () => Promise.resolve({ data: null, error: null }),
        delete: () => Promise.resolve({ data: null, error: null }),
        upsert: () => Promise.resolve({ data: null, error: null }),
      }
    },
    removeChannel: () => {
      console.warn('Mock: removeChannel called')
    },
    channel: (name: string) => {
      console.warn(`Mock: channel "${name}" created`)
      return {
        on: () => ({}),
        subscribe: () => ({}),
      }
    }
  }
  
  return mockClient as any
}

// Defensive Supabase client initialization
let supabaseClient: ReturnType<typeof createClient<Database>>

try {
  const envVarsValid = validateEnvVars()
  
  if (envVarsValid) {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
    
    // TEMPORARY: Use secureHybridStorage for all environments
    // This now uses localStorage internally but provides a consistent interface
    const storage = secureHybridStorage

    // Create real Supabase client
    supabaseClient = createClient<Database>(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true, // Enable for email confirmation callbacks
        storage: storage,
        flowType: 'pkce', // Not used for email auth but keeping for future OAuth support
        debug: import.meta.env.DEV
      },
      realtime: {
        params: {
          eventsPerSecond: 10
        }
      }
    })

    console.log('⚠️ TEMPORARY: Using localStorage-based storage for authentication')
    console.log('   📦 All tokens stored in localStorage for now')
    console.log('   🔧 TODO: Implement secure storage once auth is working')
  } else {
    // Use mock client if environment variables are missing
    supabaseClient = createMockClient()
  }
} catch (error) {
  console.error('❌ Error initializing Supabase client:', error)
  console.warn('🔧 Falling back to mock client')
  supabaseClient = createMockClient()
}

// Export the client (either real or mock)
export const supabase = supabaseClient

// Export types for use throughout the application
export type { Database } from '@/types/database' 